/**
 * The signed-in session. The refresh token lives in an HttpOnly cookie the
 * app can't read (apps/api/src/auth.ts); the short-lived ID token is held here,
 * in memory only, and fetched again from that cookie after a reload.
 */
export const CELL: string = import.meta.env.VITE_SABLE_CELL ?? "us1";
export const API_BASE = `/api/${CELL}`;

let current: { token: string; expiresAt: number } | null = null;
let inflight: Promise<string | null> | null = null;

/** A valid ID token, refreshed through the cookie when needed; null when signed out. */
export function idToken(force = false): Promise<string | null> {
  if (!force && current && current.expiresAt - 60_000 > Date.now()) return Promise.resolve(current.token);
  inflight ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/session`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "x-requested-with": "sable" },
      });
      if (!res.ok) {
        current = null;
        return null;
      }
      const body = (await res.json()) as { idToken: string; expiresIn: number };
      current = { token: body.idToken, expiresAt: Date.now() + body.expiresIn * 1000 };
      return current.token;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export type SocialProvider = "google" | "facebook";

/** The social sign-in options this cell offers; none if the call fails. */
export async function socialProviders(): Promise<SocialProvider[]> {
  try {
    const res = await fetch(`${API_BASE}/auth/providers`);
    return res.ok ? ((await res.json()) as { providers: SocialProvider[] }).providers : [];
  } catch {
    return [];
  }
}

export function signIn(options: { returnTo?: string; signUp?: boolean; provider?: SocialProvider } = {}): void {
  const q = new URLSearchParams({ return: options.returnTo ?? location.pathname + location.search });
  if (options.signUp) q.set("signup", "1");
  if (options.provider) q.set("provider", options.provider);
  location.assign(`${API_BASE}/auth/login?${q}`);
}

export async function signOut(): Promise<void> {
  current = null;
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-requested-with": "sable" },
  });
  const body = res.ok ? ((await res.json()) as { logoutUrl?: string }) : {};
  location.assign(body.logoutUrl ?? "/");
}
